import { Inject, Injectable, InjectionToken } from '@angular/core';
import { AppConfig, TokenExpiration } from '@digilize/shared/definitions/src';
import { getJwtExpiration } from '@digilize/shared/utils/helpers';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';

const MS_PER_MINUTE = 60 * 1000;

/** Epoch milliseconds `minutes` from now — plain arithmetic, where moment used to cost 141 kB. */
const minutesFromNow = (minutes: number): number => Date.now() + minutes * MS_PER_MINUTE;

export const AUTH_STORAGE = new InjectionToken<Storage>('AUTH_STORAGE');

/**
 * The webshop's own view of the session, in the `localStorage` records it shares with pauly-auth and
 * pauly-order2seat on this origin.
 *
 * The access token's expiry comes from `AppConfig.tokenExpiration` — the token's own `exp` where the app
 * asks for it. It was a hardcoded minute against the five the server grants, copied into
 * pauly-order2seat for consistency and unrelated to anything the API said, which is PAUL-3246: renewing
 * five times more often than needed churned through the account's simultaneous-session slots and evicted
 * the user's other sessions.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRepository {
  private readonly REFRESH_TOKEN_EXPIRATION = 60 * 24 * 365 * 100; // minutes (100 years)
  private readonly tokenExpiration: TokenExpiration;

  private tokenKey: string = null;
  private tokenExpirationKey: string = null;
  private refreshTokenKey: string = null;
  private refreshTokenExpirationKey: string = null;

  constructor(@Inject(AUTH_STORAGE) private storage: Storage, @Inject(APP_CONFIG) private appConfig: AppConfig) {
    const prefix = this.appConfig.localStorageAuthPrefix || '';

    this.tokenExpiration = this.appConfig.tokenExpiration;
    this.tokenKey = `${prefix}${'token'}`;
    this.tokenExpirationKey = `${prefix}${'tokenExpiration'}`;
    this.refreshTokenKey = `${prefix}${'refresh_token'}`;
    this.refreshTokenExpirationKey = `${prefix}${'refreshTokenExpiration'}`;
  }

  setToken(token: string): void {
    this.storage.setItem(this.tokenKey, token);
    this.storage.setItem(this.tokenExpirationKey, JSON.stringify(this.resolveTokenExpiration(token)));
  }

  /** The token's own `exp` where the app asks for it, and the configured lifetime otherwise. */
  private resolveTokenExpiration(token: string): number {
    const { source, minutes } = this.tokenExpiration;

    if (source === 'jwt') {
      const expiresAt = getJwtExpiration(token);

      if (expiresAt !== null) {
        return expiresAt;
      }
    }

    return minutesFromNow(minutes);
  }

  getToken(): string | null {
    return this.storage.getItem(this.tokenKey) || null;
  }

  setRefreshToken(refreshToken: string): void {
    this.storage.setItem(this.refreshTokenKey, refreshToken);
    this.storage.setItem(this.refreshTokenExpirationKey, JSON.stringify(minutesFromNow(this.REFRESH_TOKEN_EXPIRATION)));
  }

  getRefreshToken(): string | null {
    return this.storage.getItem(this.refreshTokenKey) || null;
  }

  clear(): void {
    this.storage.removeItem(this.tokenKey);
    this.storage.removeItem(this.refreshTokenKey);
    this.storage.removeItem(this.tokenExpirationKey);
    this.storage.removeItem(this.refreshTokenExpirationKey);
  }
}
